import { Avatar, Button, Card, Flex, Heading, Text, TextField } from "@radix-ui/themes";
import { type FormEvent, useEffect, useRef, useState } from "react";
import DashboardShell from "../../components/dashboard/DashboardShell";
import { CARD_STYLE } from "../../components/dashboard/tokens";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeAppearance } from "../../contexts/ThemeAppearanceContext";
import { emailInitials } from "../../utils/ui";

function providerLabel(provider: string | undefined): string {
    if (!provider) {
        return "email";
    }
    return provider.charAt(0).toUpperCase() + provider.slice(1);
}

const SettingsPage = () => {
    const { user, signOut, changePassword } = useAuth();
    const { appearance, setAppearance } = useThemeAppearance();

    const [passwordOpen, setPasswordOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [passwordNotice, setPasswordNotice] = useState("");
    const [passwordTerminal, setPasswordTerminal] = useState(false);
    const passwordButton = useRef<HTMLButtonElement>(null);
    const currentPasswordInput = useRef<HTMLInputElement>(null);
    const passwordStatus = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        if (passwordOpen) {
            currentPasswordInput.current?.focus();
        }
    }, [
        passwordOpen,
    ]);

    useEffect(() => {
        if (passwordNotice) {
            passwordStatus.current?.focus();
        }
    }, [
        passwordNotice,
    ]);

    const closePasswordForm = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordError("");
        setPasswordOpen(false);
        passwordButton.current?.focus();
    };

    const savePassword = async (event: FormEvent) => {
        event.preventDefault();
        if (savingPassword) {
            return;
        }
        setPasswordError("");
        setPasswordNotice("");
        if (newPassword !== confirmPassword) {
            setPasswordError("Passwords do not match");
            return;
        }
        if (currentPassword === newPassword) {
            setPasswordError("Choose a new password different from your current password.");
            return;
        }
        setSavingPassword(true);
        try {
            const result = await changePassword(currentPassword, newPassword, confirmPassword);
            closePasswordForm();
            setPasswordTerminal(!!result.error_description);
            setPasswordNotice(result.error_description || "Your password changed. Other app sessions have been signed out.");
        } catch (error) {
            setPasswordError(error instanceof Error ? error.message : "Password change failed. Please try again.");
        } finally {
            setSavingPassword(false);
        }
    };

    const memberSince = user?.created_at
        ? new Intl.DateTimeFormat(undefined, {
              month: "short",
              year: "numeric",
          }).format(new Date(user.created_at))
        : null;

    return (
        <DashboardShell title="Settings" subtitle="Account and appearance.">
            <Flex direction="column" gap="4">
                <Card size="3" style={CARD_STYLE}>
                    <Heading size="3" mb="4">
                        Profile
                    </Heading>
                    <Flex justify="between" align="center" gap="4" wrap="wrap">
                        <Flex align="center" gap="3">
                            <Avatar size="3" radius="full" fallback={emailInitials(user?.email)} />
                            <div>
                                <Text as="div" size="2" weight="medium">
                                    {user?.email}
                                </Text>
                                <Text as="div" size="1" color="gray">
                                    Signed in with {providerLabel(user?.app_metadata?.provider)}
                                    {memberSince ? ` · member since ${memberSince}` : ""}
                                </Text>
                            </div>
                        </Flex>
                        <Button variant="surface" color="gray" onClick={() => signOut()}>
                            Sign out
                        </Button>
                    </Flex>
                </Card>

                <Card size="3" style={CARD_STYLE}>
                    <Flex direction="column" gap="4">
                        <Flex justify="between" align="center" gap="3" wrap="wrap">
                            <div>
                                <Heading size="3" mb="1">
                                    Change password
                                </Heading>
                                <Text as="p" size="2" color="gray">
                                    Update the password you use to sign in.
                                </Text>
                            </div>
                            <Button
                                ref={passwordButton}
                                type="button"
                                variant="surface"
                                color="gray"
                                aria-expanded={passwordOpen}
                                aria-controls="change-password-form"
                                disabled={savingPassword || passwordTerminal}
                                onClick={() => {
                                    if (passwordOpen) {
                                        closePasswordForm();
                                    } else {
                                        setPasswordNotice("");
                                        setPasswordOpen(true);
                                    }
                                }}
                            >
                                {passwordOpen ? "Close" : "Change password"}
                            </Button>
                        </Flex>
                        {passwordNotice && (
                            <Text as="p" size="2" role="status" ref={passwordStatus} tabIndex={-1} color={passwordTerminal ? "amber" : "green"}>
                                {passwordNotice}
                            </Text>
                        )}
                        {passwordTerminal && (
                            <Button
                                type="button"
                                variant="surface"
                                color="gray"
                                onClick={() => signOut("signin")}
                                style={{
                                    alignSelf: "start",
                                }}
                            >
                                Sign out and sign in again
                            </Button>
                        )}
                        {passwordOpen && (
                            <form id="change-password-form" onSubmit={savePassword}>
                                <Flex
                                    direction="column"
                                    gap="3"
                                    style={{
                                        maxWidth: 440,
                                    }}
                                >
                                    <Text as="p" size="2" color="gray">
                                        Use at least 6 characters, as when signing up. Other app sessions will be signed out.
                                    </Text>
                                    <Text as="label" htmlFor="current-password" size="2" weight="medium">
                                        Current password
                                    </Text>
                                    <TextField.Root
                                        ref={currentPasswordInput}
                                        id="current-password"
                                        name="current-password"
                                        type="password"
                                        autoComplete="current-password"
                                        value={currentPassword}
                                        onChange={(event) => setCurrentPassword(event.target.value)}
                                        disabled={savingPassword}
                                        required
                                    />
                                    <Text as="label" htmlFor="new-password" size="2" weight="medium">
                                        New password
                                    </Text>
                                    <TextField.Root
                                        id="new-password"
                                        name="new-password"
                                        type="password"
                                        autoComplete="new-password"
                                        minLength={6}
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                        disabled={savingPassword}
                                        required
                                    />
                                    <Text as="label" htmlFor="confirm-password" size="2" weight="medium">
                                        Confirm new password
                                    </Text>
                                    <TextField.Root
                                        id="confirm-password"
                                        name="confirm-password"
                                        type="password"
                                        autoComplete="new-password"
                                        minLength={6}
                                        value={confirmPassword}
                                        onChange={(event) => setConfirmPassword(event.target.value)}
                                        disabled={savingPassword}
                                        required
                                    />
                                    {passwordError && (
                                        <Text as="p" size="2" color="red" role="alert">
                                            {passwordError}
                                        </Text>
                                    )}
                                    <Text as="p" size="2" color="gray">
                                        Don't have a password or forgot it?
                                    </Text>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        disabled={savingPassword}
                                        onClick={() => signOut("password-recovery")}
                                        style={{
                                            alignSelf: "start",
                                        }}
                                    >
                                        Sign out to recover password
                                    </Button>
                                    <Flex gap="2" wrap="wrap">
                                        <Button type="submit" disabled={savingPassword}>
                                            {savingPassword ? "Saving…" : "Save password"}
                                        </Button>
                                        <Button type="button" variant="surface" color="gray" disabled={savingPassword} onClick={closePasswordForm}>
                                            Cancel
                                        </Button>
                                    </Flex>
                                </Flex>
                            </form>
                        )}
                    </Flex>
                </Card>

                <Card size="3" style={CARD_STYLE}>
                    <Flex justify="between" align="center" gap="4" wrap="wrap">
                        <div>
                            <Heading size="3" mb="1">
                                Appearance
                            </Heading>
                            <Text as="p" size="2" color="gray">
                                Match the rest of your tooling.
                            </Text>
                        </div>
                        <Flex gap="2">
                            <Button variant={appearance === "dark" ? "solid" : "surface"} color="gray" onClick={() => setAppearance("dark")}>
                                Dark
                            </Button>
                            <Button variant={appearance === "light" ? "solid" : "surface"} color="gray" onClick={() => setAppearance("light")}>
                                Light
                            </Button>
                        </Flex>
                    </Flex>
                </Card>
            </Flex>
        </DashboardShell>
    );
};

export default SettingsPage;
